import {
  call,
  takeLatest,
  put,
  takeEvery,
  all,
  select
} from 'redux-saga/effects';
import PatientServices from 'shared/services/PatientServices';

import {
  buildPatientPage,
  createPatientData,
  getPatientsData,
  setPatientsData,
  actionFail,
  addNewNote,
  setIndividualPatientinfo,
  setIndividualPatientpaymentMethod,
  removeNote,
  createPatientNote,
  deletePatientNote,
  createPatientOrderSuccess,
  createOrder,
  addPaymentMethod,
  addNewCard,
  createStripeSetupIntent,
  setStripeSetupIntent,
  getPaymentMethods,
  removePaymentMethodSuccess,
  removePaymentMethod,
  updatePatientData,
  updatePatientDataSuccess,
  updatePatientDataFail,
  updateQuestionnaire,
  setUpdatedQuestionnaireSuccess,
  updatedQuestionnaireError,
  setNewAddress,
  addAddress,
  invitePatientCompleteQuestionnaire,
  setPatient,
  getOnePatient as getOnePatientAction,
  uploadPatientFile as uploadPatientFileAction,
  setPatientFiles
} from './patients.redux';
import {
  getOrder,
  updateOrderQuestionnaireInstances
} from '../orders/orders.redux';
import { IPatient } from 'shared/interfaces/patient.interface';
import {
  TAction,
  TPaginationRequest,
  TNoteCreateAction,
  TPatientAction,
  TNewOrderCreation,
  TAddPaymentMethodAction,
  TStripeIntent,
  TSetupIntentAction,
  TGetAvailablePaymentMethodsAction,
  TCreatePatient,
  TUpdatePatient,
  TInvitePatientCompleteQuestionnaireAction,
  TUploadPatientFile
} from 'shared/types';
import OrderService from 'shared/services/OrderService';
import BillingsService from 'shared/services/BillingsService';
import { IOrder } from 'shared/interfaces/order.interface';
import { IState } from 'shared/interfaces/state.interface';
import { INote } from 'shared/interfaces/note.interface';
import NoteService from 'shared/services/NoteService';
import { openNotificationMessage } from '../../shared/utils/NotificationMessages';
import {
  NotificationMessages,
  OrderStatuses,
  PaymentIntentStatuses
} from 'shared/enums';
import { IPaymentMethod } from 'shared/interfaces/payment-method.interface';
import { AxiosResponse } from 'axios';
import { IQuestionnaireInstance } from 'shared/interfaces/questionnaire-instance.interface';
import { IAddress } from 'shared/interfaces/address.interface';
import { IFile } from 'shared/interfaces/file.interface';

function* getPatients({ payload }: TAction<TPaginationRequest>) {
  try {
    const result: AxiosResponse<IPatient[]> = yield call(
      PatientServices.getPatientInfo,
      payload
    );
    yield put(setPatientsData(result.data));
  } catch (error) {
    console.log('error', error);
  }
}

function* createPatient({ payload }: TAction<TCreatePatient>) {
  try {
    yield call(PatientServices.createPatient, payload.patientData);
    yield put(getPatientsData(payload.pagination));
    yield openNotificationMessage({
      type: NotificationMessages.Success,
      title: 'Success',
      message: 'Patient was successfully created.'
    });
  } catch (error: any) {
    console.log('error', error);
    yield put(actionFail());
    yield openNotificationMessage({
      type: NotificationMessages.Error,
      title: 'Error',
      message:
        error?.response?.data.message || 'Patient creation was unsuccessful.'
    });
  }
}
function* buildIndividualPage({ payload }: TAction<TPatientAction>) {
  try {
    const [patient, orders]: [IPatient, IOrder[]] = yield all([
      call(PatientServices.getOnePatient, payload.id),
      call(OrderService.getAll, { ...payload, patient: payload.id })
    ]);

    yield put(setIndividualPatientinfo([patient, orders]));
  } catch (error) {
    console.log('error', error);
  }
}

function* getOnePatient({ payload }: TAction<TPatientAction>) {
  try {
    const patient: IPatient = yield call(
      PatientServices.getOnePatient,
      payload.id
    );
    yield put(setPatient(patient));
  } catch (error) {
    console.log('error', error);
  }
}

function* createNote({ payload }: TAction<TNoteCreateAction>) {
  try {
    const currentPatient: IPatient = yield select(
      (s: IState) => s.patients.dataIndividualPatient.data
    );

    const result: INote = yield call(NoteService.createNote, {
      ...payload,
      patient: currentPatient.id,
      relatedEntity: currentPatient.id
    });

    yield put(addNewNote(result));
    yield openNotificationMessage({
      type: NotificationMessages.Success,
      title: 'Success',
      message: 'Note was successfully added.'
    });
  } catch (error: any) {
    console.log('error', error);
    yield openNotificationMessage({
      type: NotificationMessages.Error,
      title: 'Error',
      message: error?.response?.data.message || 'Note creation was unsuccesful.'
    });
  }
}
function* deleteNote({ payload }: TAction<string>) {
  try {
    yield call(NoteService.deleteNote, payload);

    yield put(removeNote(payload));
    yield openNotificationMessage({
      type: NotificationMessages.Success,
      title: 'Success',
      message: 'Note was successfully deleted.'
    });
  } catch (error: any) {
    console.log('error', error);
    yield openNotificationMessage({
      type: NotificationMessages.Error,
      title: 'Error',
      message: error?.response?.data.message || 'Unable to delete note.'
    });
  }
}

function* createOrderPatient({ payload }: TAction<TNewOrderCreation>) {
  try {
    const result: IOrder = yield call(OrderService.createOrder, payload);

    const { isAddressInSelect } = payload.newDataCreation;
    const resultObject = { ...result, isAddressInSelect };
    yield put(createPatientOrderSuccess(resultObject));
    yield call(getOnePatient, {
      type: getOnePatientAction.type,
      payload: { id: result.patient.id }
    });
    yield openNotificationMessage({
      type: NotificationMessages.Success,
      title: 'Success',
      message: 'Order was successfully created.'
    });

    if (
      result.project.orderAutoSubmissionEnabled &&
      result.consultationPaymentIntent &&
      result.status !== OrderStatuses.Started
    ) {
      const { status } = result.consultationPaymentIntent;
      if (
        status === PaymentIntentStatuses.PaymentIntentPending ||
        status === PaymentIntentStatuses.RequiresConfirmation
      ) {
        yield openNotificationMessage({
          type: NotificationMessages.Error,
          title: 'Error',
          message:
            'The payment was unsuccessful. Please attach a valid payment method.'
        });
      }
    }
  } catch (error: any) {
    console.log('error', error);
    yield put(actionFail());
    yield openNotificationMessage({
      type: NotificationMessages.Error,
      title: 'Error',
      message:
        error?.response?.data.message || 'Order creation was unsuccessful.'
    });
  }
}

function* createSetupIntent({ payload }: TAction<TSetupIntentAction>) {
  try {
    const stripeIntent: TStripeIntent = yield call(
      BillingsService.createSetupIntent,
      payload
    );
    yield put(setStripeSetupIntent(stripeIntent));
  } catch (error) {
    console.log('error', error);
  }
}

function* addCard({ payload }: TAction<TAddPaymentMethodAction>) {
  try {
    const data: IPaymentMethod = yield call(
      BillingsService.addPaymentMethod,
      payload
    );

    yield put(addNewCard(data));
    yield openNotificationMessage({
      type: NotificationMessages.Success,
      title: 'Success',
      message: 'Credit Card was successfully added.'
    });
  } catch (error: any) {
    console.log('error', error);
    yield openNotificationMessage({
      type: NotificationMessages.Error,
      title: 'Error',
      message:
        error?.response?.data.message || 'Credit card addition was unsuccesful.'
    });
  }
}

function* removeCard({ payload }: TAction<TAddPaymentMethodAction>) {
  try {
    const paymentMethodId: string = yield call(
      BillingsService.removePaymentMethod,
      payload
    );

    yield put(removePaymentMethodSuccess({ paymentMethodId }));

    yield openNotificationMessage({
      type: NotificationMessages.Success,
      title: 'Success',
      message: 'Card was successfully removed.'
    });
  } catch (error: any) {
    console.log('error', error);
    yield openNotificationMessage({
      type: NotificationMessages.Error,
      title: 'Error',
      message: error?.response?.data.message || 'Unable to remove card.'
    });
  }
}

function* getPatientPaymentMethods({
  payload
}: TAction<TGetAvailablePaymentMethodsAction>) {
  try {
    const data: IPaymentMethod[] = yield call(
      BillingsService.getAvailablePaymentMethods,
      payload
    );

    if (data) {
      yield put(setIndividualPatientpaymentMethod(data));
    }
  } catch (error) {
    console.log('error', error);
  }
}

function* handleUpdatePatientData({ payload }: TAction<TUpdatePatient>) {
  try {
    const result: AxiosResponse<IPatient> = yield call(
      PatientServices.updatePatient,
      payload
    );
    yield put(updatePatientDataSuccess(result));
    yield openNotificationMessage({
      type: NotificationMessages.Success,
      title: 'Success',
      message: 'Patient data was successfully updated.'
    });
  } catch (error: any) {
    yield put(updatePatientDataFail());
    console.log('error', error);
    yield openNotificationMessage({
      type: NotificationMessages.Error,
      title: 'Error',
      message:
        error?.response?.data?.message || 'Patient updating was unsuccesful.'
    });
  }
}

function* handleInviteCompleteQuestionnaire({
  payload
}: TAction<TInvitePatientCompleteQuestionnaireAction>) {
  try {
    const result: AxiosResponse<IQuestionnaireInstance> = yield call(
      PatientServices.inviteCompleteQuestionnaire,
      payload
    );

    yield put(updateOrderQuestionnaireInstances(result));

    yield openNotificationMessage({
      type: NotificationMessages.Success,
      title: 'Success',
      message: 'Successfully invited patient to complete questionnaire'
    });
  } catch (error: any) {
    console.log('error', error);
    yield openNotificationMessage({
      type: NotificationMessages.Error,
      title: 'Error',
      message:
        error?.response?.data?.message ||
        'Failed to invite patient to complete questionnaire'
    });
  }
}

function* handleUpdateQuestionnaire({
  payload
}: TAction<IQuestionnaireInstance>) {
  try {
    const result: IQuestionnaireInstance = yield call(
      OrderService.updateQuestionnaireInstance,
      payload
    );
    yield put(setUpdatedQuestionnaireSuccess(result));
    if (result.valid) {
      yield openNotificationMessage({
        type: NotificationMessages.Success,
        title: 'Success',
        message: 'Questionnaire was successfully updated.'
      });

      const currentPatient: IPatient = yield select(
        (state: IState) => state.patients.dataIndividualPatient.data
      );
      if (currentPatient) {
        yield put(buildPatientPage({ id: currentPatient.id }));
      }

      const currentOrder: IOrder = yield select(
        (state: IState) => state.orders.currentOrder.data
      );
      if (currentOrder) {
        yield put(getOrder({ id: currentOrder.id, needPaymentMethod: true }));
      }
    }
  } catch (error: any) {
    yield put(updatedQuestionnaireError());
    console.log('error', error);
    yield openNotificationMessage({
      type: NotificationMessages.Error,
      title: 'Error',
      message:
        error?.response?.data?.message ||
        'Questionnaire updating was unsuccessful.'
    });
  }
}

function* addPatientAddress({ payload }: TAction<any>) {
  try {
    const data: AxiosResponse<IAddress> = yield call(
      PatientServices.addPatientAddress,
      payload
    );

    yield put(setNewAddress(data));
    yield openNotificationMessage({
      type: NotificationMessages.Success,
      title: 'Success',
      message: 'Address was successfully added.'
    });
  } catch (error: any) {
    console.log('error', error);
    yield openNotificationMessage({
      type: NotificationMessages.Error,
      title: 'Error',
      message:
        error?.response?.data.message || 'Address addition was unsuccessful.'
    });
  }
}

function* uploadPatientFile({ payload }: TAction<TUploadPatientFile>) {
  try {
    const result: IFile = yield call(
      PatientServices.uploadFile,
      payload as TUploadPatientFile
    );
    yield put(setPatientFiles(result));
    yield openNotificationMessage({
      type: NotificationMessages.Success,
      title: 'Success',
      message: 'The file was uploaded successfully'
    });
  } catch (error) {
    yield openNotificationMessage({
      type: NotificationMessages.Error,
      title: 'Error',
      message: 'The file was not uploaded'
    });
    console.log('uploadPatientFile error', error);
  }
}

function* Patients() {
  yield takeLatest(getPatientsData.type, getPatients);
  yield takeEvery(createPatientData.type, createPatient);
  yield takeLatest(buildPatientPage.type, buildIndividualPage);
  yield takeLatest(createPatientNote.type, createNote);
  yield takeLatest(deletePatientNote.type, deleteNote);
  yield takeLatest(createStripeSetupIntent.type, createSetupIntent);
  yield takeLatest(addPaymentMethod.type, addCard);
  yield takeLatest(removePaymentMethod.type, removeCard);
  yield takeLatest(getPaymentMethods.type, getPatientPaymentMethods);
  yield takeEvery(createOrder.type, createOrderPatient);
  yield takeEvery(updatePatientData.type, handleUpdatePatientData);
  yield takeEvery(updateQuestionnaire.type, handleUpdateQuestionnaire);
  yield takeEvery(
    invitePatientCompleteQuestionnaire.type,
    handleInviteCompleteQuestionnaire
  );
  yield takeLatest(addAddress.type, addPatientAddress);
  yield takeLatest(getOnePatientAction.type, getOnePatient);
  yield takeLatest(uploadPatientFileAction.type, uploadPatientFile);
}

export default Patients;
